import {ChangeDetectorRef, Component, Inject} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AdminService} from '../../services/admin.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import Swal from "sweetalert2";
import {TranslateService} from "@ngx-translate/core";
import {HttpClient} from "@angular/common/http";
import {firstValueFrom} from "rxjs";
import {PDFDocument, PDFFont, PDFPage, StandardFonts} from "pdf-lib";

interface CoachDocument {
  key: string;
  label: string;
  icon: string;
  url: string;
  downloading?: boolean;
  previewOk?: boolean;
}

@Component({
  selector: 'app-edit-coach-status',
  templateUrl: './edit-coach-status.component.html',
  styleUrls: ['./edit-coach-status.component.scss']
})
export class EditCoachStatusComponent {
  coachStatusForm: FormGroup;
  statuses = ['PENDING', 'REVIEW', 'ACCEPTED', 'REJECTED'];
  loading = false;
  coach: any;
  documents: CoachDocument[] = [];
  downloadingAll = false;

  constructor(
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private adminService: AdminService,
    private http: HttpClient,
    public dialogRef: MatDialogRef<EditCoachStatusComponent>,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: { coachApplication: any }
  ) {
    this.coach = data.coachApplication;
    this.coachStatusForm = this.fb.group({
      status: [this.coach?.status || 'PENDING', Validators.required],
      comment: [this.coach?.comment || ''],
    });

    this.coachStatusForm.get('status')?.valueChanges.subscribe(status => {
      const commentControl = this.coachStatusForm.get('comment');
      if (status === 'REJECTED' || status === 'REVIEW') {
        commentControl?.setValidators(Validators.required);
      } else {
        commentControl?.clearValidators();
      }
      commentControl?.updateValueAndValidity();
    });

    this.documents = ([
      {key: 'photo', label: 'Photo', icon: 'fa-image', url: this.coach?.photoUrl},
      {key: 'medicalCertificate', label: 'Medical Certificate', icon: 'fa-file-medical', url: this.coach?.medicalCertificateUrl},
      {key: 'noConvictionProof', label: 'No Conviction Proof', icon: 'fa-file-shield', url: this.coach?.noConvictionProofUrl},
      {key: 'oldLicense', label: 'Old License', icon: 'fa-id-card', url: this.coach?.oldLicenseUrl},
      {key: 'diplomaCopy', label: 'Diploma Copy', icon: 'fa-graduation-cap', url: this.coach?.diplomaCopyUrl},
    ] as CoachDocument[]).filter(doc => !!doc.url);
  }

  openPreview(doc: CoachDocument): void {
    window.open(doc.url, '_blank');
  }

  downloadDocument(doc: CoachDocument): void {
    if (doc.downloading) {
      return;
    }
    this.fetchAndSave(doc).then(() => this.cdRef.detectChanges());
  }

  async downloadAll(): Promise<void> {
    if (this.downloadingAll || !this.documents.length) {
      return;
    }
    this.downloadingAll = true;
    this.cdRef.detectChanges();
    try {
      const mergedPdf = await PDFDocument.create();
      const font = await mergedPdf.embedFont(StandardFonts.HelveticaBold);
      for (const doc of this.documents) {
        doc.downloading = true;
        this.cdRef.detectChanges();
        try {
          await this.appendDocumentToPdf(mergedPdf, doc, font);
        } catch {
          window.open(doc.url, '_blank');
        } finally {
          doc.downloading = false;
          this.cdRef.detectChanges();
        }
      }
      const pdfBytes = await mergedPdf.save();
      const fileName = `${this.coach?.firstName || ''} ${this.coach?.lastName || ''}`.trim() || 'coach';
      this.saveBlob(new Blob([pdfBytes], {type: 'application/pdf'}), `${fileName} - documents.pdf`);
    } finally {
      this.downloadingAll = false;
      this.cdRef.detectChanges();
    }
  }

  private async appendDocumentToPdf(mergedPdf: PDFDocument, doc: CoachDocument, font: PDFFont): Promise<void> {
    const response = await firstValueFrom(
      this.http.get(doc.url, {responseType: 'arraybuffer', observe: 'response'})
    );
    const arrayBuffer = response.body as ArrayBuffer;
    const contentType = response.headers.get('content-type') || '';
    const urlPath = doc.url.split('?')[0].toLowerCase();
    const isPdf = contentType.includes('pdf') || urlPath.endsWith('.pdf');

    if (isPdf) {
      const srcPdf = await PDFDocument.load(arrayBuffer, {ignoreEncryption: true});
      const pages = await mergedPdf.copyPages(srcPdf, srcPdf.getPageIndices());
      pages.forEach(page => mergedPdf.addPage(page));
      return;
    }

    let image;
    try {
      image = await mergedPdf.embedJpg(arrayBuffer);
    } catch {
      image = await mergedPdf.embedPng(arrayBuffer);
    }

    const maxWidth = 595;
    const maxHeight = 780;
    const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
    const width = image.width * scale;
    const height = image.height * scale;
    const headerHeight = 30;

    const page = mergedPdf.addPage([Math.max(width, 260), height + headerHeight]);
    page.drawImage(image, {x: 0, y: 0, width, height});
    this.labelPage(page, doc.label, font);
  }

  private labelPage(page: PDFPage, label: string, font: PDFFont): void {
    const {height} = page.getSize();
    page.drawText(label, {
      x: 10,
      y: height - 20,
      size: 12,
      font,
    });
  }

  private fetchAndSave(doc: CoachDocument): Promise<void> {
    doc.downloading = true;
    this.cdRef.detectChanges();
    return new Promise(resolve => {
      this.http.get(doc.url, {responseType: 'blob'}).subscribe({
        next: (blob) => {
          this.saveBlob(blob, this.fileNameFromUrl(doc.url, doc.label));
          doc.downloading = false;
          resolve();
        },
        error: () => {
          window.open(doc.url, '_blank');
          doc.downloading = false;
          resolve();
        }
      });
    });
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(objectUrl);
  }

  private fileNameFromUrl(url: string, fallback: string): string {
    const lastSegment = url.split('?')[0].split('/').pop();
    return lastSegment || fallback;
  }

  updateStatus() {
    if (this.coachStatusForm.valid) {
      const status = this.coachStatusForm.get('status')?.value;
      const comment = this.coachStatusForm.get('comment')?.value;

      this.translate.get([
        'Are you sure?',
        'Do you want to accept this coach application?',
        'Do you want to reject this coach application?',
        'Do you want to mark this coach application for review?',
        'Yes',
        'No',
        'Status Updated',
        'The coach application status has been successfully updated!',
        'Error',
        'There was an issue updating the status. Please try again later.',
        'OK'
      ]).subscribe(translations => {
        let confirmText = translations['Do you want to reject this coach application?'];
        if (status === 'ACCEPTED') {
          confirmText = translations['Do you want to accept this coach application?'];
        } else if (status === 'REVIEW') {
          confirmText = translations['Do you want to mark this coach application for review?'];
        }

        Swal.fire({
          title: translations['Are you sure?'],
          text: confirmText,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: translations['Yes'],
          cancelButtonText: translations['No'],
        }).then((result) => {
          if (result.isConfirmed) {
            this.loading = true;
            this.adminService.updateCoachApplicationStatus(this.data.coachApplication.id, status, comment).subscribe({
              next: () => {
                this.loading = false;
                Swal.fire({
                  icon: 'success',
                  title: translations['Status Updated'],
                  text: translations['The coach application status has been successfully updated!'],
                  confirmButtonText: translations['OK'],
                }).then(() => {
                  this.dialogRef.close(true);
                });
              },
              error: () => {
                Swal.fire({
                  title: translations['Error'],
                  text: translations['There was an issue updating the status. Please try again later.'],
                  icon: 'error',
                  confirmButtonText: translations['OK'],
                }).then(() => {
                  this.loading = false;
                  this.cdRef.detectChanges();
                });
              },
            });
          }
        });
      });
    }
  }

  cancel() {
    this.dialogRef.close();
  }
}
